import { SessionRow } from "../DeclarationCourse/SessionList";

/**
 * Déclaration de cours v2 : état unique de l'assistant, persisté par Redux
 * (clé `declarationV2`). Copie de la logique métier de `DeclarationCourse`
 * sans en modifier les écrans : l'ancien parcours reste disponible sur
 * `/declare`, le nouveau vit sur `/declarer`.
 */

export type DeclarationStep = "home" | "student" | "planning" | "sessions" | "pricing" | "summary" | "success";

export type CourseType = "single" | "multiple";

/** Élève (ou client) choisi à l'étape 1, tel que renvoyé par l'API des clients actifs. */
export interface SelectedStudent {
    childId: number;
    /** Fiche brute de l'API (childFirstName, clientId, addressId, quotas, montants…). */
    child: any;
    /** Date de validation du compte d'avance immédiate (ISO), borne basse des dates de cours. */
    activationDate: string | null;
}

export interface SinglePlanning {
    /** Jour du cours (YYYY-MM-DD). */
    date: string | null;
    /** Heure de début (ISO datetime, seule l'heure compte). */
    startTime: string | null;
    /** Durée en heures. */
    duration: number;
}

export interface DepositInfo {
    enabled: boolean;
    amount: string;
    /** Date de réception (ISO). */
    date: string | null;
    attestationAccepted: boolean;
    /** Preuve de versement (objet image de l'API : id, contentUrl, filePath). */
    proof: any | null;
}

export interface PricingInfo {
    /** Matière choisie (objet Subject : id, fr_FR). Verified Trainer : identifiant fixe 108. */
    subject: any | null;
    /** Prix horaire payé par le client, saisi par le prof. */
    price: string;
    deposit: DepositInfo;
}

export interface DeclarationV2State {
    step: DeclarationStep;
    courseType: CourseType | null;
    student: SelectedStudent | null;
    /** Déroulement propre à ce cours, null = description habituelle. */
    sessionDescriptionOverride: string | null;
    single: SinglePlanning;
    sessions: SessionRow[];
    note: string;
    pricing: PricingInfo;
    legalAccepted: boolean;
    /** Identifiant du cours créé, pour le devis de l'écran de succès. */
    declaredCourseId: number | null;
    /** Date de la déclaration réussie (ISO), pour l'échéance des 48 h. */
    declaredAt: string | null;
    /** Dernière mise à jour (ISO), affichée sur « Reprendre où j'en étais ». */
    updatedAt: string | null;
    /** Étape où le parcours s'est arrêté, proposée en reprise depuis l'accueil. */
    resumeStep: DeclarationStep | null;
    /** Séances dont le début a été décalé après un cours existant à la génération, à signaler sur l'écran des séances. */
    shiftedSessions?: ShiftedSessionNote[];
}

export interface ShiftedSessionNote {
    key: string;
    date: string;
    /** « 17:00 » */
    to: string;
    studentName: string;
}

export const DEFAULT_DURATION_HOURS = 1.5;

export const emptyDeposit = (): DepositInfo => ({
    enabled: false,
    amount: "",
    date: null,
    attestationAccepted: false,
    proof: null,
});

export const initialDeclarationV2State = (): DeclarationV2State => ({
    step: "home",
    courseType: null,
    student: null,
    sessionDescriptionOverride: null,
    single: { date: null, startTime: null, duration: DEFAULT_DURATION_HOURS },
    sessions: [],
    note: "",
    pricing: { subject: null, price: "", deposit: emptyDeposit() },
    legalAccepted: false,
    declaredCourseId: null,
    declaredAt: null,
    updatedAt: null,
    resumeStep: null,
    shiftedSessions: [],
});

/** Numéro d'étape affiché dans le rail (1 à 4), null pour l'accueil et le succès. */
export const stepNumber = (step: DeclarationStep): number | null => {
    switch (step) {
        case "student": return 1;
        case "planning":
        case "sessions": return 2;
        case "pricing": return 3;
        case "summary": return 4;
        default: return null;
    }
};

/** Étape précédente dans le parcours, selon le type de cours. */
export const previousStep = (step: DeclarationStep, courseType: CourseType | null): DeclarationStep => {
    switch (step) {
        case "student": return "home";
        case "planning": return "student";
        case "sessions": return "planning";
        case "pricing": return courseType === "multiple" ? "sessions" : "planning";
        case "summary": return "pricing";
        default: return "home";
    }
};

/** Un parcours est « en cours » quand un élève a été choisi et rien n'a encore été déclaré. */
export const hasDeclarationInProgress = (state: DeclarationV2State | null | undefined): boolean =>
    !!state && state.student !== null && state.declaredCourseId === null && state.step !== "success";
